import Vue from 'vue';
import Component from 'vue-class-component';

import Content from '@/content/models/content.model';
import KEYS from '@/values/keys';
import { MENU_IDS, MY_CONTENT_ITEM_MENU, MY_CONTENT_TYPES } from '@/app/private/my-content/values/my-content.values';
import { EdContextMenuOptionsModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { Prop } from 'vue-property-decorator';
import { MY_CONTENT_EVENTS } from '@/content/values/events';
import { MY_CONTENT_STORE } from '@/app/private/my-content/store/my-content.store';
import { IResponseConfirmationModal } from '@/plugins/ed-modal/interfaces/response-confirmation-modal.interface';
import { CORE_ROUTES } from '@/core/core.router';
import EdShareCourseComponent from '@/classes/components/ed-share-course/ed-share-course.component.vue'
import { BUILDER_STATUS, BUILDER_SYNC_STATUS } from '@/courses/values/courses.values';
import { FILESCACHE_KEYS } from '@/shared/plugins/files-cache/enums/files-cache.enum';
import { WORKER_EVENT_BUS } from '@/core/enums/worker.enum';

@Component({
  name: 'ed-content-item',
})
export default class EdContentItemComponent extends Vue {

  @Prop() readonly content: Content;
  @Prop() readonly page: string;
  @Prop() readonly openMenuItem: Function;

  public isActionInProcess: boolean = false;
  public actions = MY_CONTENT_EVENTS;
  public onMouseOver: boolean = false;
  public statusTimeout = null;
  public draftTag = null;
  public syncStatus = null;
  public conflictTag = null;

  get authors() {
    return `${this.$i18n.t('myContent.by')} ${this.content.authorsNames}`;
  }

  get listMenu() {
    return this.getMenu(MY_CONTENT_TYPES.COURSES);
  }

  get overMenu() {
    return [this.listMenu[0]];
  }
  
  get progressLabel() {
    if (this.content.isCopying) {
      return `${this.$i18n.t('myContent.contentItem.copying')}...`;
    }

    if (this.content.hasError) {
      return `${this.$i18n.t('myContent.contentItem.error')}...`;
    }

    return '';
  }

  get progressColor() {
    if (this.content.hasError) {
      return 'var(--ed-color-danger)';
    }

    return '';
  }
  
  getMenu(type: string): any[] {
    let menu: any[] = MY_CONTENT_ITEM_MENU[this.page+'_'+type];

    if (this.content.hasError || this.content.isCopying) {
      menu = menu.filter(m => 
        m.id === MENU_IDS.REMOVE || m.id === MENU_IDS.SEE_PROCESS
      );
    } else if(this.content.status === BUILDER_STATUS.DRAFT) {
      menu = menu.filter(m => m.id === MENU_IDS.SUBJECT_BUILDER || m.id === MENU_IDS.REMOVE);
      this.draftTag = `${this.$i18n.t('myContent.draft')}`;
      this.syncStatus = this.content.syncStatus;
    } else if(this.content.status === BUILDER_STATUS.PENDING) {
      this.syncStatus = BUILDER_SYNC_STATUS.UPLOADING;
      this.draftTag = `${this.$i18n.t('myContent.uploading')}`;
      menu = menu.filter(m => m.id === MENU_IDS.VIEW);
    } else if(this.content.status === BUILDER_STATUS.CONFLICT) {
      this.conflictTag = `${this.$i18n.t('myContent.conflict')}`;
      menu = menu.filter(m => m.id === MENU_IDS.RESOLVE_CONFLICTS);
    } else if(this.content.status === BUILDER_STATUS.DONE && this.content.builderId) {
      this.syncStatus = this.content.syncStatus;
      menu = menu.filter(m => m.id !== MENU_IDS.SEE_PROCESS && m.id !== MENU_IDS.MAKE_TEMPLATE && m.id !== MENU_IDS.EDIT_CONTENT && m.id !== MENU_IDS.EDIT_PRODUCT && m.id !== MENU_IDS.RESOLVE_CONFLICTS);
    } else {
      menu = menu.filter(m => m.id !== MENU_IDS.SEE_PROCESS && m.id !== MENU_IDS.SUBJECT_BUILDER &&  m.id !== MENU_IDS.RESOLVE_CONFLICTS);
    }

    return menu.map((m: any) => {
      return {
        ...m,
        text: String(this.$i18n.t(m.text))
      }
    });
  }

  listMenuSelected(optionSelected: EdContextMenuOptionsModel) {
    switch(optionSelected.id){
      case MENU_IDS.VIEW:
        this.goToViewPage(this.content);
      break;
      case MENU_IDS.DOWNLOAD:
        this.goToDownload(this.content);
      break;
      case MENU_IDS.REMOVE:
        this.removeContent(this.content);
      break;
      case MENU_IDS.EDIT_CONTENT:
        this.editContent(this.content);
      break;
      case MENU_IDS.EDIT_PRODUCT:
        this.editProduct(this.content);
      break;
      case MENU_IDS.MAKE_TEMPLATE:
        this.makeTemplate(this.content);
      break;
      case MENU_IDS.COURSE_ADMIN:
        this.courseAdminModal(this.content);
      break;
      case MENU_IDS.SEE_PROCESS:
        this.openBuildingProcess();
      break;
      case MENU_IDS.SUBJECT_BUILDER:
        this.goToBuildSubject(this.content);
        break;
    }
  }

  openBuildingProcess() {
    this.$EdModal.createModal(
      () => import(/* EdSubjectBuildingProcessModal */'@/courses/components/ed-subject-builder/ed-subject-building-process-modal/ed-subject-building-process-modal.component.vue'),
      {},
      {
        contentId: this.content.id,
        processFailed: this.content.hasError,
      }
    );
  }

  goToViewPage(content: Content) {
    if(content.viewPage) {
      this.$router.push({
        name: CORE_ROUTES.COURSE.NAME,
        params: {
          courseId: content.id
        }
      });
    }
  }

  goToDownload(content: Content) {
    window.location.href = String(content.urlDownload);
  }

  removeContent(content: Content) {
    this.$EdModal.createConfirmationModal({
      body: this.$i18n.t('myContent.confirmRemoveDraft'),
      action: async () => {
        this.$edEventBus.$emit(MY_CONTENT_EVENTS.ACTION_IN_PROCESS, {content, inProcess: true});
        if(content.deleteRemote) {
          this.$store.dispatch(MY_CONTENT_STORE.ACTIONS.REMOVE_CONTENT, content).catch(() => {
                    this.$edEventBus.$emit(MY_CONTENT_EVENTS.ACTION_IN_PROCESS, {content, inProcess: false});
          });
        } else {
          this.$store.dispatch(MY_CONTENT_STORE.ACTIONS.REMOVE_CONTENT_FROM_STORE, content).catch(() => {
            this.$edEventBus.$emit(MY_CONTENT_EVENTS.ACTION_IN_PROCESS, {content, inProcess: false});
          });
        }
        //Delete all data from indexDB related with Subject builder
        if(content.deleteLocal)  {
          await this.$Builder.Services.deleteAllDataFromIndexDB(content.id);
        }
      }
    });
  }

  editContent(content: Content) {
    this.$router.push({ 
      name: CORE_ROUTES.COURSE.NAME,
      params: {
        courseId: content.id,
        mode: 'edit'
      } 
    });
  }

  editProduct(content: Content) {
    this.$router.push({
      name: CORE_ROUTES.CREATE_COURSE.NAME,
      params: {
        id: content.id
      }
    });
  }

  makeTemplate(content: Content) {
    if(content.empty) {
      this.$EdModal.createConfirmationModal({
        body: this.$i18n.t('myContent.makeTemplate.confirmMsg'),
        actionText: this.$i18n.t('myContent.makeTemplate.confirmOk'),
        action: () => { this.editContent(content); }
      });
    } else {
      /* Confirm public/private template */
      this.$EdModal.createConfirmationModal({
        body: String(this.$i18n.t('myContent.makeTemplate.confirmTemplate')),
        hasOptionRadio: true,
        firstOptionRadioText: String(this.$i18n.t('myContent.makeTemplate.private')),
        secondOptionRadioText: String(this.$i18n.t('myContent.makeTemplate.public')),
        actionText: String(this.$i18n.t('common.create')),
        action: (res: IResponseConfirmationModal) => {
          this.createTemplate(res.optionRadioValue == 'second');
        }
      });
    }
  }

  goToBuildSubject(content: Content) {
    this.$router.push({
      name: CORE_ROUTES.SUBJECT_BUILDER_V2.NAME,
      params: {id: (content.fromId) ? content.fromId : content.id},
      query: {builderId: (content.builderId) ? content.builderId : content.id, type: content.type, classId: content.classId, syncStatus: this.syncStatus}
    });
  }

  async createTemplate(isPublic: boolean) {
    setTimeout(async () => {
      try {
        this.isActionInProcess = true;
        await this.$CourseTemplates.Store.createCourseTemplate(
          { courseId: this.content.id, isPublic }
        );
        this.isActionInProcess = false;
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('myContent.makeTemplate.createdTitle'),
          body: this.$i18n.t('myContent.makeTemplate.createdBody'),
          type: KEYS.EVENT_TYPES.SUCCESS
        });
      } catch(err) {
        this.isActionInProcess = false;
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('myContent.makeTemplate.errorBody'),
          type: KEYS.EVENT_TYPES.DANGER
        });
      }
    }, 300);
  }

  courseAdminModal(data){
    this.$EdModal.createModal(EdShareCourseComponent, { course: data });
  }

  updateSyncBuilderStatus({builderId, status}) {
    if(this.content.id === builderId) {

      if(this.content.status === BUILDER_STATUS.PENDING) {
        this.content.syncStatus = BUILDER_SYNC_STATUS.UPLOADING;
        this.syncStatus = BUILDER_SYNC_STATUS.UPLOADING;
      } else {
        this.content.syncStatus = status;
        this.syncStatus = status;
      }
    }
  }

  updateBuilderStatus({builderId, status}) {
    if(this.content.id === builderId) {
      this.content.status = status;
      this.draftTag = null;
    }
  }

  async checkContentStatus() {
    if (this.content.isCopying) {
      this.statusTimeout = setTimeout(async() => {
        await this.$store.dispatch(MY_CONTENT_STORE.ACTIONS.GET_CONTENT_STATUS_BY_ID, this.content.id);
        this.checkContentStatus();
      }, 5000);
    }
  }

  created() {
    this.checkContentStatus();
    this.$edEventBus.$on(MY_CONTENT_EVENTS.ACTION_IN_PROCESS, ({content, inProcess}) => {
      this.isActionInProcess = content.id == this.content.id ? inProcess : false;
    });

    this.$edEventBus.$on(WORKER_EVENT_BUS.SYNC_BUILDER_STATUS, this.updateSyncBuilderStatus);
  }

  destroyed() {
    if (this.statusTimeout) {
      clearTimeout(this.statusTimeout);
    }
  }

  beforeDestroy() {
    this.$edEventBus.$off(WORKER_EVENT_BUS.SYNC_BUILDER_STATUS, this.updateSyncBuilderStatus);
  }

}
